import { useContext, useMemo } from 'react';
import { Chart as ChartJS } from 'chart.js';
import { ThemeContext } from '../context/ThemeContext';
import { resolveTokens } from './palette';
import { MAX_SERIES, deriveSeries, onFill, wash } from './chartColors';

// =============================================================
// The chart theme: every Chart.js chart reads its colours here.
//
// WHY A HOOK AND NOT var(--pg-*): Chart.js paints a <canvas>, and a
// canvas has no stylesheet. `backgroundColor: 'var(--pg-accent)'` is a
// string Chart.js cannot parse, so the palette never reached a chart --
// every one of them was frozen on Classic while the page around it
// moved. The values have to be real colours, resolved in JS.
//
// WHY FROM THE PALETTE SELECTION AND NOT getComputedStyle(:root):
// ThemeContext paints :root in a useEffect, which runs AFTER its
// children render. A chart reading computed styles during render would
// read the palette the user just left. resolveTokens() is the same pure
// function that paint uses, so this is the new palette on the same
// render -- no flash of the old one, no timing to get wrong.
//
// THE SPLIT, the same as every stylesheet in the app:
//   decoration  follows the palette  -- series slot 1 (the accent),
//               the surface a gap or ring is cut from, grid, axis line
//   meaning     stays literal        -- ink for every piece of text, and
//               status colours (good/fair/poor, rating tones), which a
//               chart takes from its caller and never from here
//
// See instruction_ui.json "charts" for the rules a chart must follow.
// =============================================================

/** The ink ladder. Text in a chart never wears a series colour. */
export const CHART_INK = {
  strong: '#101828',
  body: '#475467',
  muted: '#667085',
  faint: '#98A2B3',
  ghost: '#C2CAD8',
};

const FONT_FALLBACK = "'Epilogue', 'Segoe UI', system-ui, sans-serif";

// Chart.js draws its own text with its own default font (Helvetica).
// Pointed once at the app's body face, so axis ticks and tooltips are
// the same Epilogue as the page. Read from --font-body rather than typed
// again here, so a change of face is one change in tokens.css.
let defaultsApplied = false;
const applyChartDefaults = () => {
  if (defaultsApplied || typeof document === 'undefined') return;
  defaultsApplied = true;
  const family =
    getComputedStyle(document.documentElement).getPropertyValue('--font-body').trim() ||
    FONT_FALLBACK;
  ChartJS.defaults.font.family = family;
  ChartJS.defaults.color = CHART_INK.muted;
};

// A scale's grid, border and ticks are nested objects; a caller adding a
// stepSize must not wipe out the tick colour, so they merge one level down
// (and ticks.font one further).
const mergeScale = (base, extra = {}) => ({
  ...base,
  ...extra,
  grid: { ...base.grid, ...(extra.grid || {}) },
  border: { ...base.border, ...(extra.border || {}) },
  ticks: {
    ...base.ticks,
    ...(extra.ticks || {}),
    font: { ...base.ticks.font, ...((extra.ticks && extra.ticks.font) || {}) },
  },
});

/** Build the theme from a resolved --pg-* token map. Exported for tests. */
export const buildChartTheme = (tokens = {}) => {
  // --pg-card is white in every palette (utils/palette.js pins the card at
  // L 100, S 0), but it is read rather than assumed: it is the ground every
  // gap and point ring is cut from.
  const surface = tokens['--pg-card'] || '#FFFFFF';
  const grid = tokens['--pg-line-soft'] || '#EDF0F6';
  const axis = tokens['--pg-line'] || '#E6EAF2';
  // Always MAX_SERIES long, so a chart can index slot 4 without a guard. A
  // slot the accent left no distinct hue for is muted grey, never a hue that
  // fails the gates.
  const derived = deriveSeries(tokens['--pg-accent'] || '#3B6FF0', surface);
  const series = Array.from({ length: MAX_SERIES }, (_, i) => derived[i] || CHART_INK.muted);

  return {
    /** Series colours by slot. Slot 1 is the accent. Assign by measure, never by rank. */
    series,
    surface,
    grid,
    axis,
    ink: CHART_INK,
    wash,
    onFill: (fill) => onFill(fill, CHART_INK.strong),

    /** Spread into plugins.tooltip, then add callbacks. */
    tooltip: {
      backgroundColor: CHART_INK.strong,
      titleColor: '#FFFFFF',
      bodyColor: '#FFFFFF',
      padding: 10,
      cornerRadius: 6,
      titleFont: { size: 12, weight: '700' },
      bodyFont: { size: 12 },
      displayColors: false,
    },

    /** The axis that carries the numbers: hairline grid, no border line, faint ticks. */
    valueAxis: (extra) =>
      mergeScale(
        {
          grid: { color: grid, lineWidth: 1 },
          border: { display: false },
          ticks: { color: CHART_INK.faint, font: { size: 11.5 } },
        },
        extra,
      ),

    /** The axis that carries the names: no grid, one axis line, body ink. */
    categoryAxis: (extra) =>
      mergeScale(
        {
          grid: { display: false },
          border: { color: axis },
          ticks: { color: CHART_INK.body, font: { size: 11.5 } },
        },
        extra,
      ),

    /** plugins.legend.labels: a round swatch carries the hue, the words stay in ink. */
    legendLabels: (extra = {}) => ({
      boxWidth: 9,
      boxHeight: 9,
      usePointStyle: true,
      pointStyle: 'circle',
      color: CHART_INK.body,
      font: { size: 11.5 },
      padding: 16,
      ...extra,
    }),
  };
};

/**
 * The current palette as chart colours. A new object only when the palette
 * changes, so it is safe -- and required -- as a useMemo dependency of any
 * chart's data and options: that dependency is what repaints the chart when
 * someone picks a palette.
 */
export const useChartTheme = () => {
  const palette = useContext(ThemeContext)?.palette;
  applyChartDefaults();
  return useMemo(() => buildChartTheme(resolveTokens(palette)), [palette]);
};
